import { useCallback, useEffect, useMemo, useState } from "react";

import { ItemDataType } from "components/Tree/types";

import { SearchFilterType, SearchSortType } from "./DatabaseSearchMenu";

enum TreeNodeTypeEnum {
  Database = "database",
  Table = "table",
  ExternalTable = "external_table",
  InformationSchemaTable = "information_schema_table",
  View = "view",
  Column = "column",
  InformationSchemaColumn = "information_schema_column",
  ViewColumn = "view_column",
  ExternalTableColumn = "external_table_column",
  Index = "index",
}

const isTableType = (type?: string) =>
  type === TreeNodeTypeEnum.Table || type === TreeNodeTypeEnum.ExternalTable;

const isColumnType = (type?: string) =>
  type === TreeNodeTypeEnum.Column ||
  type === TreeNodeTypeEnum.InformationSchemaColumn ||
  type === TreeNodeTypeEnum.ViewColumn ||
  type === TreeNodeTypeEnum.ExternalTableColumn;

type SearchCounts = {
  total: number;
  databases: number;
  tables: number;
  views: number;
  columns: number;
  indexes: number;
};

type Result = {
  filteredTreeData: ItemDataType[];
  expandedOnSearch: string[];
  searchCounts: SearchCounts;
  filterType: SearchFilterType;
  sortType: SearchSortType;
  setFilterType: (next: SearchFilterType) => void;
  setSortType: (next: SearchSortType) => void;
  resetSearchOptions: () => void;
};

type Params = {
  fullSearchData: ItemDataType[];
  newSearchExperience: boolean;
  searchTerm: string;
};

export const useSearchFilterSort = ({
  fullSearchData,
  newSearchExperience,
  searchTerm,
}: Params): Result => {
  const [filterType, setFilterType] = useState<SearchFilterType>(
    SearchFilterType.All
  );
  const [sortType, setSortType] = useState<SearchSortType>(
    SearchSortType.Alphabetical
  );

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();

  const { filteredTreeData, expandedOnSearch } = useMemo(() => {
    if (!newSearchExperience) {
      return { filteredTreeData: [], expandedOnSearch: [] };
    }
    if (!normalizedSearchTerm) {
      return { filteredTreeData: fullSearchData, expandedOnSearch: [] };
    }

    const expandedValues = new Set<string>();

    const allowedTypes = (() => {
      switch (filterType) {
        case SearchFilterType.Databases:
          return new Set([TreeNodeTypeEnum.Database]);
        case SearchFilterType.Tables:
          return new Set([
            TreeNodeTypeEnum.Table,
            TreeNodeTypeEnum.ExternalTable,
          ]);
        case SearchFilterType.Views:
          return new Set([
            TreeNodeTypeEnum.View,
            TreeNodeTypeEnum.InformationSchemaTable,
          ]);
        case SearchFilterType.Columns:
          return new Set([
            TreeNodeTypeEnum.Column,
            TreeNodeTypeEnum.InformationSchemaColumn,
            TreeNodeTypeEnum.ViewColumn,
            TreeNodeTypeEnum.ExternalTableColumn,
          ]);
        case SearchFilterType.Indexes:
          return new Set([TreeNodeTypeEnum.Index]);
        default:
          return null;
      }
    })();

    const filterNodeType = (nodeType?: string) => {
      if (!nodeType || !allowedTypes) {
        return true;
      }
      return allowedTypes.has(nodeType as TreeNodeTypeEnum);
    };

    const filterNode = (node: ItemDataType): ItemDataType | null => {
      const label = node.label?.toLowerCase() || "";
      const matches =
        label.includes(normalizedSearchTerm) && filterNodeType(node.type);

      let filteredChildren: ItemDataType[] | null | undefined = node.children;
      if (Array.isArray(node.children)) {
        filteredChildren = node.children
          .map(filterNode)
          .filter(Boolean) as ItemDataType[];
      }

      const hasMatchingChild =
        Array.isArray(filteredChildren) && filteredChildren.length > 0;
      const includeAllChildrenOnMatch =
        matches &&
        (node.type === TreeNodeTypeEnum.Database ||
          node.type === "schema" ||
          isTableType(node.type) ||
          node.type === TreeNodeTypeEnum.View ||
          node.type === TreeNodeTypeEnum.InformationSchemaTable);
      const childrenToReturn = includeAllChildrenOnMatch
        ? node.children
        : filteredChildren;

      if (matches || hasMatchingChild) {
        if (hasMatchingChild && node.value) {
          expandedValues.add(node.value);
        }
        return { ...node, children: childrenToReturn };
      }

      return null;
    };

    const filtered = fullSearchData
      .map(filterNode)
      .filter(Boolean) as ItemDataType[];

    const getTimestamp = (value?: string | null) => {
      if (!value) {
        return 0;
      }
      const time = Date.parse(value);
      return Number.isNaN(time) ? 0 : time;
    };

    const compareNodes = (a: ItemDataType, b: ItemDataType) => {
      if (sortType === SearchSortType.Alphabetical) {
        return a.label.localeCompare(b.label, undefined, { sensitivity: "base" });
      }

      const left =
        sortType === SearchSortType.LastModified
          ? getTimestamp((a.payload as { lastAltered?: string })?.lastAltered)
          : getTimestamp((a.payload as { created?: string })?.created);
      const right =
        sortType === SearchSortType.LastModified
          ? getTimestamp((b.payload as { lastAltered?: string })?.lastAltered)
          : getTimestamp((b.payload as { created?: string })?.created);

      if (left !== right) {
        return right - left;
      }
      return a.label.localeCompare(b.label, undefined, { sensitivity: "base" });
    };

    const sortNodes = (nodes: ItemDataType[]): ItemDataType[] => {
      if (nodes.length < 2) {
        return nodes;
      }
      return nodes.slice().sort(compareNodes);
    };

    return {
      filteredTreeData:
        normalizedSearchTerm.length > 0 ? sortNodes(filtered) : filtered,
      expandedOnSearch: Array.from(expandedValues),
    };
  }, [
    filterType,
    fullSearchData,
    normalizedSearchTerm,
    newSearchExperience,
    sortType,
  ]);

  const searchCounts = useMemo(() => {
    if (!newSearchExperience || !normalizedSearchTerm) {
      return {
        total: 0,
        databases: 0,
        tables: 0,
        views: 0,
        columns: 0,
        indexes: 0,
      };
    }

    const counts = {
      total: 0,
      databases: 0,
      tables: 0,
      views: 0,
      columns: 0,
      indexes: 0,
    };

    const visit = (node: ItemDataType) => {
      const label = node.label?.toLowerCase() || "";
      if (label.includes(normalizedSearchTerm)) {
        counts.total += 1;
        if (node.type === TreeNodeTypeEnum.Database) {
          counts.databases += 1;
        } else if (isTableType(node.type)) {
          counts.tables += 1;
        } else if (node.type === TreeNodeTypeEnum.View) {
          counts.views += 1;
        } else if (isColumnType(node.type)) {
          counts.columns += 1;
        } else if (node.type === TreeNodeTypeEnum.Index) {
          counts.indexes += 1;
        }
      }

      if (Array.isArray(node.children)) {
        node.children.forEach(child => visit(child));
      }
    };

    fullSearchData.forEach(node => visit(node));

    return counts;
  }, [fullSearchData, newSearchExperience, normalizedSearchTerm]);

  const resetSearchOptions = useCallback(() => {
    setFilterType(SearchFilterType.All);
    setSortType(SearchSortType.Alphabetical);
  }, []);

  useEffect(() => {
    if (!newSearchExperience || !normalizedSearchTerm) {
      resetSearchOptions();
    }
  }, [newSearchExperience, normalizedSearchTerm, resetSearchOptions]);

  return {
    filteredTreeData,
    expandedOnSearch,
    searchCounts,
    filterType,
    sortType,
    setFilterType,
    setSortType,
    resetSearchOptions,
  };
};
